import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import {
  Speed as SpeedIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { useReadingTempoStore, getAvgTimeMs, getPacePerHour } from '../../stores/readingTempoStore';
import { gwtStyles } from '../../config/theme';

/**
 * Reading Tempo Widget — small inline display in the header bar showing
 * the radiologist's reading pace for the current session.
 *
 * Compact view:  📊 12 · 3m 08s
 * Tooltip:       Full session stats with reset option
 *
 * Updates every 15 seconds (cheap — just re-reads Zustand + derives).
 */
export const ReadingTempoWidget = ({ queueRemaining }: { queueRemaining?: number }) => {
  const completions = useReadingTempoStore((s) => s.completions);
  const sessionStartMs = useReadingTempoStore((s) => s.sessionStartMs);
  const reset = useReadingTempoStore((s) => s.reset);

  // Tick every 15s to keep elapsed time and pace fresh
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, []);

  const count = completions.length;
  const avgMs = getAvgTimeMs();
  const pace = getPacePerHour();
  const sessionMs = now - sessionStartMs;

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    reset();
  }, [reset]);

  // Don't show anything until at least 1 study completed
  if (count === 0) {
    return (
      <Tooltip title="Reading tempo — complete a study to start tracking">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.4, px: 0.5 }}>
          <SpeedIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ fontSize: 11, color: gwtStyles.textMuted }}>
            0 read
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const avgFormatted = avgMs != null ? formatDuration(avgMs) : '—';
  const paceFormatted = pace != null ? pace.toFixed(1) : '—';
  const sessionFormatted = formatDuration(sessionMs);
  const etaFormatted = pace != null && queueRemaining != null && pace > 0
    ? formatDuration((queueRemaining / pace) * 3_600_000)
    : null;

  // Compact label
  const compactLabel = avgMs != null
    ? `${count} · ${avgFormatted}`
    : `${count} read`;

  const tooltipContent = (
    <Box sx={{ p: 0.5, minWidth: 180 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: 12, display: 'block', mb: 0.5 }}>
        Reading Tempo
      </Typography>
      <Box component="table" sx={{ fontSize: 11, '& td': { py: 0.15 }, '& td:first-of-type': { pr: 1.5, color: gwtStyles.textMuted } }}>
        <tbody>
          <tr><td>Session</td><td>{sessionFormatted}</td></tr>
          <tr><td>Studies read</td><td><b>{count}</b></td></tr>
          {avgMs != null && <tr><td>Avg time/study</td><td><b>{avgFormatted}</b></td></tr>}
          {pace != null && <tr><td>Pace</td><td>{paceFormatted}/hr</td></tr>}
          {queueRemaining != null && <tr><td>Queue remaining</td><td>{queueRemaining}</td></tr>}
          {etaFormatted != null && <tr><td>Est. to clear</td><td>{etaFormatted}</td></tr>}
        </tbody>
      </Box>
      <Box sx={{ mt: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
        <IconButton size="small" onClick={handleReset} sx={{ fontSize: 10, p: 0.25 }}>
          <ResetIcon sx={{ fontSize: 12, mr: 0.25 }} />
          <Typography variant="caption" sx={{ fontSize: 10 }}>Reset</Typography>
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      arrow
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: gwtStyles.popupBg,
            border: `1px solid ${gwtStyles.popupBorder}`,
            color: gwtStyles.textPrimary,
            maxWidth: 260,
          },
        },
        arrow: {
          sx: { color: gwtStyles.popupBg },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          cursor: 'default',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
        }}
      >
        <SpeedIcon sx={{ fontSize: 14, color: gwtStyles.accentPrimary }} />
        <Typography
          variant="caption"
          sx={{ fontSize: 11, fontWeight: 500, color: gwtStyles.textSecondary, whiteSpace: 'nowrap' }}
        >
          {compactLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/* ── Helpers ── */

/** Format ms duration as human-readable: "3m 08s", "1h 24m", etc. */
function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${String(mins).padStart(2, '0')}m`;
}
